import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * SystemPicker
 *
 * 自定义字段：从 Backstage Catalog 动态加载 System 列表
 *
 * 使用方式：
 * ```yaml
 * system:
 *   title: 所属系统
 *   type: string
 *   ui:field: SystemPicker
 * ```
 *
 * 特点：
 * - 从 Catalog API 获取所有 System 实体
 * - 显示 System 名称和描述
 * - 支持关联 ArgoCD Project（通过 annotation）
 */
export const SystemPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const [systems, setSystems] = useState<
    Array<{
      name: string;
      description: string;
      owner: string;
      argocdProjectRef?: string;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadSystems = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取所有 System 实体
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'System',
          },
        });

        const systemList = response.items.map(entity => ({
          name: entity.metadata.name,
          description: entity.metadata.description || '',
          owner: entity.spec?.owner as string || '',
          argocdProjectRef:
            entity.metadata.annotations?.['hose.io/argocd-project-ref'],
        }));

        // 按名称排序
        systemList.sort((a, b) => a.name.localeCompare(b.name));

        setSystems(systemList);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load systems');
      } finally {
        setLoading(false);
      }
    };

    loadSystems();
  }, [catalogApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'System'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && systems.length === 0 && (
          <MenuItem value="" disabled>
            无可用系统（请先创建 System）
          </MenuItem>
        )}
        {systems.map(system => (
          <MenuItem key={system.name} value={system.name}>
            {system.name}
            {system.description && ` - ${system.description}`}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};

